import React from "react";
import {
  Box,
  Typography,
  Container,
  Toolbar,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  Pagination,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Skeleton,
  Fade,
  useMediaQuery,
} from "@mui/material";
import { Search as SearchIcon, FilterList as FilterIcon } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import API from "../../../API";
import { useTheme } from '@mui/material/styles';


const ArticleSkeleton = () => (
  <Card 
    sx={{ 
      borderRadius: 3, 
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "12px 12px 0 0" }} />
    <CardContent sx={{ flexGrow: 1 }}>
      <Skeleton variant="text" height={28} width="90%" sx={{ mb: 1 }} />
      <Skeleton variant="text" height={20} width="100%" />
      <Skeleton variant="text" height={20} width="95%" />
      <Skeleton variant="text" height={20} width="85%" />
      <Box sx={{ mt: 3, display: "flex", gap: 1 }}>
        <Skeleton variant="rounded" width={70} height={28} />
        <Skeleton variant="rounded" width={90} height={28} />
      </Box>
      <Skeleton variant="text" height={16} width="60%" sx={{ mt: 2 }} />
    </CardContent>
  </Card>
);

const Articles = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = React.useState(parseInt(searchParams.get("page")) || 1);
  const [searchQuery, setSearchQuery] = React.useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = React.useState(searchParams.get("category") || "");
  const [selectedTag, setSelectedTag] = React.useState(searchParams.get("tag") || "");
  const [sortBy, setSortBy] = React.useState(searchParams.get("sort") || "published_at");
  const [showFeaturedOnly, setShowFeaturedOnly] = React.useState(searchParams.get("featured") === "true");

  const { data: categoriesData } = useQuery(
    "articleCategories",
    () => API.articles.categories(),
    { staleTime: 300000 }
  );

  const { data: tagsData } = useQuery(
    "articleTags",
    () => API.articles.tags(),
    { staleTime: 300000 }
  );

  const categories = categoriesData?.data?.categories || [];
  const tags = tagsData?.data?.tags || [];

  const categoryParam = React.useMemo(() => {
    if (!selectedCategory) return undefined;
    const match = categories.find(
      (c) => c.slug === selectedCategory || c.name === selectedCategory
    );
    return match ? match.name : selectedCategory;
  }, [selectedCategory, categories]);

  const {
    data: articlesData,
    isLoading: articlesLoading,
    isError: articlesError,
  } = useQuery(
    ["articles", page, searchQuery, categoryParam, selectedTag, sortBy, showFeaturedOnly],
    () =>
      API.articles.list({
        page,
        per_page: 12,
        search: searchQuery || undefined,
        category: categoryParam,
        tag: selectedTag || undefined,
        sort: sortBy,
        featured: showFeaturedOnly || undefined,
      }),
    {
      keepPreviousData: true,
      staleTime: 30000,
    }
  );

  const articles = articlesData?.data?.articles || [];
  const pagination = articlesData?.data?.pagination || {};

  React.useEffect(() => {
    const params = {};
    if (page > 1) params.page = page;
    if (searchQuery) params.search = searchQuery;
    if (selectedCategory) params.category = selectedCategory;
    if (selectedTag) params.tag = selectedTag;
    if (sortBy !== "published_at") params.sort = sortBy;
    if (showFeaturedOnly) params.featured = "true";
    setSearchParams(params);
  }, [page, searchQuery, selectedCategory, selectedTag, sortBy, showFeaturedOnly, setSearchParams]);

  const handlePageChange = (_event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleArticleClick = (slug) => navigate(`/articles/${slug}`);

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
    setSelectedTag("");
    setSortBy("published_at");
    setShowFeaturedOnly(false);
    setPage(1);
  };

  const activeFiltersCount = [
    searchQuery,
    selectedCategory,
    selectedTag,
    sortBy !== "published_at" ? sortBy : null,
    showFeaturedOnly,
  ].filter(Boolean).length;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <Box sx={{ backgroundColor: "#fafbfc", width: "100%", minHeight: "100vh" }}>
      <HomeHeader />
      <Toolbar />

      {/* Hero Section - Enhanced with modern gradient */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Fade in timeout={800}>
            <Box>
              <Typography 
                variant={isMobile ? "h4" : "h3"} 
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  letterSpacing: "-0.02em",
                }}
              >
                Articles & Insights
              </Typography>
              <Typography 
                variant={isMobile ? "body1" : "h6"} 
                sx={{ 
                  opacity: 0.95, 
                  mb: 4,
                  maxWidth: 700,
                  fontWeight: 400,
                }}
              >
                Discover the latest trends, tips, and insights in influencer marketing
              </Typography>

              {/* Enhanced Search Bar */}
              <TextField
                fullWidth
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: 3,
                  maxWidth: 650,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  "& .MuiOutlinedInput-root": { 
                    "& fieldset": { border: "none" },
                    borderRadius: 3,
                    py: 0.5,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#667eea", fontSize: 28 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
        {/* Enhanced Filters Section */}
        <Paper 
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mt: { xs: -4, md: -6 },
            mb: 4, 
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            position: "relative",
            zIndex: 2,
            background: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <FilterIcon sx={{ color: "#667eea" }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
              Filters
            </Typography>
            {activeFiltersCount > 0 && (
              <Chip 
                label={`${activeFiltersCount} active`} 
                size="small" 
                sx={{ 
                  bgcolor: "#667eea",
                  color: "white",
                  fontWeight: 600,
                }} 
              />
            )}
            {activeFiltersCount > 0 && (
              <Button 
                size="small" 
                onClick={handleClearFilters} 
                sx={{ 
                  ml: "auto",
                  textTransform: "none",
                  color: "#667eea",
                  fontWeight: 600,
                }}
              >
                Clear all
              </Button>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setPage(1);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {(categories || []).map((category) => (
                    <MenuItem key={category.slug} value={category.slug}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tag</InputLabel>
                <Select
                  value={selectedTag}
                  label="Tag"
                  onChange={(e) => {
                    setSelectedTag(e.target.value);
                    setPage(1);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Tags</MenuItem>
                  {(tags || []).map((tag) => (
                    <MenuItem key={tag} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="published_at">Latest</MenuItem>
                  <MenuItem value="view_count">Most Popular</MenuItem>
                  <MenuItem value="title">Title (A-Z)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant={showFeaturedOnly ? "contained" : "outlined"}
                onClick={() => {
                  setShowFeaturedOnly((v) => !v);
                  setPage(1);
                }}
                sx={{ 
                  height: "40px",
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  ...(showFeaturedOnly && {
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "none",
                  }),
                }}
              >
                Featured Only
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Articles Grid */}
        {articlesLoading ? (
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {Array.from({ length: 12 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <ArticleSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : articlesError ? (
          <Paper 
            sx={{ 
              p: 6, 
              textAlign: "center", 
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
              Failed to load articles
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please try again or adjust your filters.
            </Typography>
          </Paper>
        ) : articles.length === 0 ? (
          <Paper 
            sx={{ 
              p: 6, 
              textAlign: "center",
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
              No articles found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your filters or search query
            </Typography>
          </Paper>
        ) : (
          <>
            <Fade in timeout={600}>
              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                {articles.map((article, index) => (
                  <Grid item xs={12} sm={6} md={4} key={article.id}>
                    <Fade in timeout={400 + index * 50}>
                      <Card
                        sx={{
                          borderRadius: 3,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          border: "1px solid",
                          borderColor: "transparent",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
                            borderColor: "rgba(102, 126, 234, 0.3)",
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleArticleClick(article.slug)}
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                          }}
                        >
                          {article.featured_image && (
                            <Box sx={{ position: "relative", overflow: "hidden" }}>
                              <CardMedia
                                component="img"
                                height="200"
                                image={article.featured_image}
                                alt={article.featured_image_alt || article.title}
                                sx={{ 
                                  objectFit: "cover",
                                  transition: "transform 0.3s ease-in-out",
                                  "&:hover": {
                                    transform: "scale(1.05)",
                                  },
                                }}
                              />
                              {article.is_featured && (
                                <Chip 
                                  label="Featured" 
                                  size="small" 
                                  sx={{ 
                                    position: "absolute",
                                    top: 12,
                                    left: 12,
                                    bgcolor: "rgba(102, 126, 234, 0.95)",
                                    color: "white",
                                    fontWeight: 600,
                                    backdropFilter: "blur(8px)",
                                  }} 
                                />
                              )}
                            </Box>
                          )}
                          <CardContent 
                            sx={{ 
                              flexGrow: 1, 
                              display: "flex", 
                              flexDirection: "column",
                              p: { xs: 2, sm: 3 },
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                lineHeight: 1.3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                color: "#1a1a1a",
                                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                              }}
                            >
                              {article.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#666",
                                mb: 2.5,
                                flexGrow: 1,
                                lineHeight: 1.6,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {article.excerpt}
                            </Typography>

                            <Box sx={{ mt: "auto" }}>
                              <Stack 
                                direction="row" 
                                spacing={1} 
                                sx={{ mb: 1.5, flexWrap: "wrap", gap: 0.5 }}
                              >
                                {!!article.category && (
                                  <Chip 
                                    label={article.category} 
                                    size="small" 
                                    sx={{
                                      bgcolor: "rgba(102, 126, 234, 0.08)",
                                      color: "#667eea",
                                      fontWeight: 600,
                                      border: "none",
                                    }}
                                  />
                                )}
                                {!!article.read_time_minutes && (
                                  <Chip 
                                    label={`${article.read_time_minutes} min read`} 
                                    size="small" 
                                    sx={{
                                      bgcolor: "rgba(0,0,0,0.05)",
                                      color: "#666",
                                      fontWeight: 500,
                                      border: "none",
                                    }}
                                  />
                                )}
                              </Stack>

                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: "#999",
                                  fontWeight: 500,
                                }}
                              >
                                {formatDate(article.published_at)}
                                {article.author?.name ? ` • ${article.author.name}` : ""}
                              </Typography>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Fade>

            {/* Enhanced Pagination */}
            {pagination.pages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 5, md: 8 } }}>
                <Pagination
                  count={pagination.pages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? "medium" : "large"}
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: 2,
                      fontWeight: 600,
                      "&.Mui-selected": {
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>

      <HomeFooter />
    </Box>
  );
};

export default Articles;